import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { ArrowBack, GetApp } from "@material-ui/icons";
import { format, startOfMonth, subMonths } from "date-fns";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";

import Title from "../ui/Title.js";
import { DatePicker } from "../ui/DatePicker.js";
import { useBmapi } from "../utils/bmapi-context.js";
import { getErrorMessageString } from "../utils/errors.js";
import { MANAGER_ROUTES } from "../utils/constants.js";
import { common } from "../messages/index.js";

// --------------------------------------------------
// Tipologie di statistiche
// --------------------------------------------------

const STATISTIC_TYPES = [
  { code: "hot-users", label: "Utenti caldi" },
  { code: "prizes-issued", label: "Premi emessi" },
  { code: "prizes-expired", label: "Premi scaduti" },
];

// --------------------------------------------------
// Formattazione
// --------------------------------------------------

const formatRefDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatMonth = (value) => formatRefDate(value).slice(0, 7);

const formatNumber = (value) =>
  new Intl.NumberFormat("it-IT").format(value ?? 0);

const escapeCSV = (value) => {
  const text = String(value ?? "");

  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// --------------------------------------------------
// Componente principale
// --------------------------------------------------

export default function ComputedStatistics() {
  const intl = useIntl();
  const history = useHistory();

  const { bmapi, notifyError, startLoading, stopLoading } = useBmapi();

  // Primo giorno del mese precedente.
  const defaultFrom = useMemo(() => startOfMonth(subMonths(new Date(), 1)), []);

  const [code, setCode] = useState("hot-users");
  const [dateFrom, setDateFrom] = useState(defaultFrom);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtri client-side.
  const [nameFilter, setNameFilter] = useState("");
  const [filterContains, setFilterContains] = useState(true);
  const [showOnlyPresent, setShowOnlyPresent] = useState(true);

  const requestId = useRef(0);

  // --------------------------------------------------
  // Navigazione
  // --------------------------------------------------

  const goToHome = () => {
    history.push(MANAGER_ROUTES.HOME_MENU);
  };

  // --------------------------------------------------
  // Caricamento statistiche
  // --------------------------------------------------

  const loadData = useCallback(
    async (statisticCode, date) => {
      if (!bmapi || !date) return;

      const currentRequest = ++requestId.current;

      setRecords([]);
      setLoading(true);
      startLoading();

      try {
        const resp = await bmapi.getComputedStatistics({
          code: statisticCode,
          fromDate: format(date, "yyyy-MM-dd"),
        });

        if (currentRequest === requestId.current) {
          setRecords(
            Array.isArray(resp)
              ? resp.filter((record) => record.Code === statisticCode)
              : []
          );
        }
      } catch (err) {
        if (currentRequest === requestId.current) {
          setRecords([]);
          notifyError(getErrorMessageString(err, intl));
        }
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false);
        }

        stopLoading();
      }
    },
    [bmapi, intl, notifyError, startLoading, stopLoading]
  );

  // --------------------------------------------------
  // Caricamento iniziale
  // --------------------------------------------------

  useEffect(() => {
    loadData("hot-users", defaultFrom);
  }, [loadData, defaultFrom]);

  // --------------------------------------------------
  // Cambio tipologia statistica
  // --------------------------------------------------

  const handleChangeCode = (event) => {
    const newCode = event.target.value;

    setCode(newCode);

    // Reset dei filtri client-side.
    setNameFilter("");
    setFilterContains(true);
    setShowOnlyPresent(true);

    setRecords([]);

    // Tutte e tre le statistiche sono abilitate.
    loadData(newCode, dateFrom);
  };

  // --------------------------------------------------
  // Filtro e ordinamento client-side
  // --------------------------------------------------

  const sortedRecords = useMemo(() => {
    const filter = nameFilter.trim().toLocaleLowerCase("it");

    return records
      .filter((record) => {
        // Nessun filtro aggiuntivo per hot-users.
        if (code === "hot-users") {
          return true;
        }

        // ------------------------------------------
        // Filtro per nome campagna
        // ------------------------------------------

        if (filter) {
          const item = (record.Item || "").toLocaleLowerCase("it");

          const contains = item.includes(filter);

          if (filterContains ? !contains : contains) {
            return false;
          }
        }

        // ------------------------------------------
        // Mostra solo premi presenti
        // ------------------------------------------

        // Values[0] = numero premi.
        // Se selezionato, esclude i record con count <= 0.

        if (showOnlyPresent && Number(record.Values?.[0] ?? 0) <= 0) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Data decrescente.
        const dateDiff = formatRefDate(b.RefDate).localeCompare(
          formatRefDate(a.RefDate)
        );

        if (dateDiff !== 0) {
          return dateDiff;
        }

        // Campagna in ordine alfabetico.
        return (a.Item || "").localeCompare(b.Item || "", "it", {
          sensitivity: "base",
        });
      });
  }, [records, code, nameFilter, filterContains, showOnlyPresent]);

  // --------------------------------------------------
  // Ricerca
  // --------------------------------------------------

  const search = () => {
    if (!dateFrom || loading) return;

    loadData(code, dateFrom);
  };

  // --------------------------------------------------
  // Esportazione CSV
  // --------------------------------------------------

  const downloadCSV = () => {
    let rows;

    if (code === "hot-users") {
      rows = [["sett.(ult.gg)", "ut.unici 4s", "ut.unici 8s", "ut.unici 13s"]];

      sortedRecords.forEach((record) => {
        const values = record.Values || [];

        rows.push([
          formatRefDate(record.RefDate),
          values[0] ?? 0,
          values[1] ?? 0,
          values[2] ?? 0,
        ]);
      });
    } else {
      const countLabel =
        code === "prizes-issued" ? "premi emessi" : "premi scaduti";

      rows = [["mese", "campagna", countLabel, "valore totale"]];

      sortedRecords.forEach((record) => {
        const values = record.Values || [];

        rows.push([
          formatMonth(record.RefDate),
          record.Item || "",
          values[0] ?? 0,
          values[1] ?? 0,
        ]);
      });
    }

    const csvContent = rows
      .map((row) => row.map(escapeCSV).join(","))
      .join("\r\n");

    const blob = new Blob(["\uFEFF", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `sarca-${code}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // --------------------------------------------------
  // Tabella statistiche
  // --------------------------------------------------

  const renderTable = () => {
    const isHotUsers = code === "hot-users";
    const isIssued = code === "prizes-issued";

    const title = isHotUsers
      ? "Utenti caldi"
      : isIssued
      ? "Premi emessi"
      : "Premi scaduti";

    return (
      <Box mb={4}>
        <Card>
          <CardContent>
            {/* Titolo e numero record */}
            <Box mb={2}>
              <Typography variant="h5">{title}</Typography>

              <Typography variant="caption" color="textSecondary">
                {isHotUsers ? "Numero settimane" : "Record trovati"}:{" "}
                {sortedRecords.length}
              </Typography>

              {/* Filtri client-side per premi */}
              {!isHotUsers && (
                <Box
                  mt={2}
                  mb={1}
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {/* Nome campagna */}
                  <Box width={400} maxWidth="100%" mr={2}>
                    <TextField
                      label="Filtra per nome"
                      value={nameFilter}
                      onChange={(event) => setNameFilter(event.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </Box>

                  {/* Contiene / Non contiene */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filterContains}
                        onChange={(event) =>
                          setFilterContains(event.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={filterContains ? "Contiene" : "Non contiene"}
                  />

                  {/* Solo record con count > 0 */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showOnlyPresent}
                        onChange={(event) =>
                          setShowOnlyPresent(event.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Mostra solo i premi presenti"
                  />
                </Box>
              )}
            </Box>

            {/* Tabella */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {isHotUsers ? (
                      <>
                        <TableCell>
                          <strong>Settimana (ultimo giorno)</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>Utenti unici 4s</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>Utenti unici 8s</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>Utenti unici 13s</strong>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <strong>Mese</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Campagna</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>
                            {isIssued ? "Premi emessi" : "Premi scaduti"}
                          </strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>Valore totale</strong>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRecords.map((record, index) => {
                    const values = record.Values || [];

                    return (
                      <TableRow key={record.ID || index} hover>
                        {isHotUsers ? (
                          <>
                            <TableCell>
                              {formatRefDate(record.RefDate)}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(values[0])}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(values[1])}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(values[2])}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{formatMonth(record.RefDate)}</TableCell>

                            <TableCell>{record.Item || "-"}</TableCell>

                            <TableCell align="right">
                              {formatNumber(values[0])}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(values[1])}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}

                  {!loading && sortedRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Nessun record trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Esportazione CSV */}
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={downloadCSV}
            disabled={sortedRecords.length === 0 || loading}
          >
            Esporta CSV
          </Button>
        </Box>
      </Box>
    );
  };

  // --------------------------------------------------
  // Render principale
  // --------------------------------------------------

  return (
    <Container maxWidth="lg">
      <Title>Statistiche Sarca</Title>

      {/* Torna alla home - sopra */}
      <Box my={2}>
        <Button onClick={goToHome} startIcon={<ArrowBack />}>
          {intl.formatMessage(common.backHome)}
        </Button>
      </Box>

      {/* Filtri: card stretta e centrata */}
      <Container maxWidth="sm">
        <Box mb={4}>
          <Card>
            <CardContent>
              <Typography variant="h5">Seleziona statistiche</Typography>

              <Box my={2}>
                <TextField
                  label="Tipo statistica"
                  select
                  fullWidth
                  margin="dense"
                  value={code}
                  onChange={handleChangeCode}
                >
                  {STATISTIC_TYPES.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box mb={2}>
                <DatePicker
                  label="Data di inizio"
                  value={dateFrom}
                  onChange={(date) => date && setDateFrom(date)}
                  fullWidth
                  margin="dense"
                  required
                  clearable={false}
                />
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={search}
                disabled={!dateFrom || loading}
              >
                Cerca
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Risultati */}
      {renderTable()}

      {/* Torna alla home - sotto */}
      <Box my={2}>
        <Button onClick={goToHome} startIcon={<ArrowBack />}>
          {intl.formatMessage(common.backHome)}
        </Button>
      </Box>
    </Container>
  );
}
